// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
import { Disposable } from "relay-runtime";

// autopilot-feed-status imports
import { IContainer, IProps, IState } from "./AutopilotFeedStatus.d";
import { autopilotFeedStatusFragments, refetchQuery } from "./AutopilotFeedStatus.gql";
import { styles } from "./AutopilotFeedStatus.styles";

// state
import { IReduxState } from "@reducers/root.reducer";
import { setActiveViewAutopilotList } from "@actions/chrome-extension.actions";

// util
import { track, TrackingEvents } from "@util/analytics.util";

// components
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import { AutopilotFeedStatusUnstarted } from "./autopilot-feed-status-unstarted";
import { AutopilotFeedStatusQueued } from "./autopilot-feed-status-queued";
import { AutopilotFeedStatusRunning } from "./autopilot-feed-status-running";
import { AutopilotFeedStatusFinished } from "./autopilot-feed-status-finished";

// subscriptions
import { updateOrgFlowStateSubscription } from "@subscriptions";

// material-ui
import withStyles from "@material-ui/core/styles/withStyles";

enum DefragRunFlowState {
  Unstarted = "Unstarted",
  Queued = "Queued",
  Running = "Running",
  Finished = "Finished",
}

///////////////
// Component //
///////////////

class AutopilotFeedStatusCmpt extends React.Component<IProps, IState> {
  public static defaultProps = {
    isActionable: true,
  };

  private refetchTimeout?: ReturnType<typeof setTimeout>;
  private orgFlowStateDisposable?: Disposable;
  private sleepCheckInterval?: ReturnType<typeof setInterval>;

  constructor(props: IProps) {
    super(props);

    this.state = {
      refetching: false,
    };
  }

  /////////////
  // Helpers //
  /////////////

  private subscribe = (flowState: ISchema.IFlowState) => {
    if (this.orgFlowStateDisposable) {
      this.orgFlowStateDisposable.dispose();
      delete this.orgFlowStateDisposable;
    }

    this.orgFlowStateDisposable = updateOrgFlowStateSubscription(this.props.relay.environment, {
      flowStateRelayId: flowState.id,
    });
  };

  private refetch = () => {
    const { org } = this.props;

    this.setState({
      refetching: true,
    });

    if (!org) {
      this.setState({
        refetching: false,
      });
      return;
    }

    this.refetchTimeout = setTimeout(() => {
      this.setState({ refetching: false });
    }, 5000);

    this.props.relay.refetch(
      {
        orgRelayId: org.id,
      },
      undefined,
      () => {
        if (this.refetchTimeout) {
          clearTimeout(this.refetchTimeout);
          delete this.refetchTimeout;
        }
        this.setState({ refetching: false });
      },
      { force: true },
    );
  };

  //////////////
  // Handlers //
  //////////////

  private onClick = () => {
    const { isActionable, org } = this.props;

    if (!isActionable) {
      return;
    }

    const autopilotFlowState =
      org &&
      org.orgFlowStates &&
      org.orgFlowStates.__typename !== "GraphEntityError" &&
      org.orgFlowStates.list &&
      org.orgFlowStates.list.find((flowState) => flowState.flowKey === "DefragRun");

    const status = autopilotFlowState ? autopilotFlowState.current.state : "Unknown";

    track(TrackingEvents.DEFRAG.CLICK_AUTOPILOT_FEED_STATUS, { status });

    this.props.setActiveViewAutopilotList();
  };

  ///////////////
  // Lifecycle //
  ///////////////

  public componentDidMount() {
    const { org } = this.props;

    const autopilotFlowState =
      org &&
      org.orgFlowStates &&
      org.orgFlowStates.__typename !== "GraphEntityError" &&
      org.orgFlowStates.list &&
      org.orgFlowStates.list.find((flowState) => flowState.flowKey === "DefragRun");

    if (autopilotFlowState) {
      this.subscribe(autopilotFlowState);
    }

    let lastCheck = Date.now();

    // detect cpu sleep so we can update our flow state after its presumably gone stale
    this.sleepCheckInterval = setInterval(() => {
      const now = Date.now();

      if (now - lastCheck >= 60000) {
        this.refetch();
      }

      lastCheck = now;
    }, 30000);
  }

  public componentWillUnmount() {
    if (this.orgFlowStateDisposable) {
      this.orgFlowStateDisposable.dispose();
      delete this.orgFlowStateDisposable;
    }

    if (this.sleepCheckInterval) {
      clearInterval(this.sleepCheckInterval);
      delete this.sleepCheckInterval;
    }

    if (this.refetchTimeout) {
      clearTimeout(this.refetchTimeout);
      delete this.refetchTimeout;
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { org } = this.props;
    const { org: nextOrg } = nextProps;

    const autopilotFlowState =
      org &&
      org.orgFlowStates &&
      org.orgFlowStates.__typename !== "GraphEntityError" &&
      org.orgFlowStates.list &&
      org.orgFlowStates.list.find((flowState) => flowState.flowKey === "DefragRun");

    const nextAutopilotFlowState =
      nextOrg &&
      nextOrg.orgFlowStates &&
      nextOrg.orgFlowStates.__typename !== "GraphEntityError" &&
      nextOrg.orgFlowStates.list &&
      nextOrg.orgFlowStates.list.find((flowState) => flowState.flowKey === "DefragRun");

    if (nextOrg && autopilotFlowState !== nextAutopilotFlowState) {
      if (!autopilotFlowState && nextAutopilotFlowState) {
        this.subscribe(nextAutopilotFlowState);
      }

      if (
        nextAutopilotFlowState &&
        nextAutopilotFlowState.current.state === DefragRunFlowState.Finished
      ) {
        this.refetch();
      }
    }
  }

  ///////////////
  // Rendering //
  ///////////////

  private renderBody(flowState: ISchema.IFlowState) {
    const { isActionable, org, orgTimeZone, user } = this.props;

    if (!org || this.state.refetching) {
      return (
        <div
          onClick={this.onClick}
          className={this.props.classes.container}
          style={{ justifyContent: "center" }}
        >
          <CircularProgress variant="small" />
        </div>
      );
    }

    // do not show to users that are less than 12 hours old
    const state =
      (user &&
        Date.now() - user.createdTime <= 12 * 60 * 60 * 1000 &&
        DefragRunFlowState.Unstarted) ||
      flowState.current.state;

    switch (state) {
      case DefragRunFlowState.Unstarted:
        return (
          <AutopilotFeedStatusUnstarted
            onClick={this.onClick}
            isActionable={isActionable}
            org={org}
            orgTimeZone={orgTimeZone}
          />
        );
      case DefragRunFlowState.Queued:
        return <AutopilotFeedStatusQueued isActionable={isActionable} onClick={this.onClick} />;
      case DefragRunFlowState.Running:
        return <AutopilotFeedStatusRunning isActionable={isActionable} onClick={this.onClick} />;
      case DefragRunFlowState.Finished:
        return (
          <AutopilotFeedStatusFinished
            onClick={this.onClick}
            isActionable={isActionable}
            flowState={flowState}
            org={org}
            orgTimeZone={orgTimeZone}
          />
        );
      default:
        return null;
    }
  }

  public render() {
    const { isActionable, org } = this.props;

    const autopilotFlowState =
      org &&
      org.orgFlowStates &&
      org.orgFlowStates.__typename !== "GraphEntityError" &&
      org.orgFlowStates.list &&
      org.orgFlowStates.list.find((flowState) => flowState.flowKey === "DefragRun");

    if (!autopilotFlowState) {
      return null;
    }

    return (
      <div style={{ cursor: isActionable ? "pointer" : "unset", userSelect: "none" }}>
        {this.renderBody(autopilotFlowState)}
      </div>
    );
  }
}

/////////////////
// With Styles //
/////////////////

export const AutopilotFeedStatusStyled = withStyles(styles)(AutopilotFeedStatusCmpt);

///////////
// Redux //
///////////

function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

export const AutopilotFeedStatusRedux = connect(mapStateToProps, {
  setActiveViewAutopilotList,
})(AutopilotFeedStatusStyled);

//////////////////
// RELAY
//////////////////
export const AutopilotFeedStatus = createRefetchContainer(
  AutopilotFeedStatusRedux,
  autopilotFeedStatusFragments,
  refetchQuery,
);
